<!-- PlanTrailActions.vue -->
<template>
  <aside
    class="action-panel"
    :class="{ collapsed: isCollapsed }"
    :aria-labelledby="headingId"
    role="complementary"
  >
    <!-- ───────────────────── Header ───────────────────── -->
    <header class="panel-header">
      <button
        v-if="panelView === 'actions'"
        class="icon-btn"
        :aria-label="$t('plan_trail.actions.collapse_actions_panel')"
        @click="toggleCollapse"
      >
        <span class="material-icons">right_panel_close</span>
      </button>
      <button
        v-else
        class="icon-btn"
        :aria-label="$t('plan_trail.actions.back_to_actions')"
        @click="panelView = 'actions'"
      >
        <span class="material-icons">chevron_left</span>
      </button>

      <h2 v-if="panelView === 'actions'" :id="headingId" class="panel-title">{{ $t('plan_trail.actions.actions') }}</h2>
      <div v-else class="panel-title-group">
        <h2 :id="headingId" class="panel-title">{{ $t('plan_trail.actions.turn_by_turn') }}</h2>
        <p class="panel-subtitle">{{ turnInstructionsSubtitle }}</p>
      </div>

      <!-- The unit system is an account setting, not a control here — it says which units
           the distances below are in, and Settings is where it changes. -->
      <span v-if="panelView === 'instructions'" class="unit-badge">{{ unitLabel }}</span>
    </header>
    
    <div class="actions-main" v-show="panelView === 'actions'">
      <div class="panel-section d-flex">
        <div class="routing-group">
          <button class="routing-btn" @click="undoClicked">
            <span class="material-icons">undo</span>
          </button>
          <button class="routing-btn" @click="redoClicked">
            <span class="material-icons">redo</span>
          </button>
        </div>
      </div>

      <hr class="section-divider" />

    <!-- ───────────────────── Routing ───────────────────── -->
    <div class="panel-section">
      <p class="section-eyebrow">{{ $t('plan_trail.actions.routing') }}</p>
      <div class="routing-group">
        <button
          v-for="mode in routingModes"
          :key="mode.value"
          class="routing-btn"
          :class="{ active: selectedMode === mode.value }"
          :title="mode.tooltip"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          @click="setRoutingMode(mode.value)"
        >
          <span class="material-icons">{{ mode.icon }}</span>
        </button>
      </div>
    </div>

    <hr class="section-divider" />

    <!-- ───────────────────── Route options ───────────────────── -->
    <div class="panel-section">
      <p class="section-eyebrow">{{ $t('shared.route_options') }}</p>
      <div class="routing-group">
        <button
          class="routing-btn"
          @click="loopRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :title="$t('plan_trail.actions.loop')"
          :aria-label="$t('plan_trail.actions.loop_2')"
        >
          <span class="material-icons">cached</span>
        </button>
        <button
          class="routing-btn"
          @click="reverseRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :title="$t('plan_trail.actions.reverse')"
          :aria-label="$t('plan_trail.actions.reverse_2')"
        >
          <span class="material-icons">sync_alt</span>
        </button>
        <button
          class="routing-btn"
          @click="backtrackRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :title="$t('plan_trail.actions.out_and_back')"
          :aria-label="$t('plan_trail.actions.out_and_back_2')"
        >
          <span class="material-icons">keyboard_return</span>
        </button>
        <!-- Admin only: re-draw the active route along the path network so it gets turns. -->
        <button
          v-if="canMapMatchRoutes"
          class="routing-btn"
          :disabled="isMapMatching"
          @click="mapMatchRoute"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :title="$t('plan_trail.actions.map_match_admin')"
          :aria-label="$t('plan_trail.actions.map_match_this_route_onto_paths')"
        >
          <span class="material-icons">{{ isMapMatching ? 'hourglass_top' : 'alt_route' }}</span>
        </button>
      </div>
      <p v-if="mapMatchMessage" class="map-match-message">{{ mapMatchMessage }}</p>
    </div>

    <div class="panel-section">
      <p class="section-eyebrow">{{ $t('plan_trail.actions.tools') }}</p>
      <div class="tool-list">
        <button
            class="tool-list__item"
            @click="addTracerRoute"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.add_a_line_to_the_map')"
          >
          <span class="material-icons tool-list__icon">gesture</span>
          <span class="tool-list__label">{{ $t('plan_trail.actions.add_trace') }}</span>
        </button>
        <button
            class="tool-list__item"
            @click="showAddPointModal"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.add_a_control_point_by_entering')"
          >
          <span class="material-icons tool-list__icon">add_location</span>
          <span class="tool-list__label">{{ $t('plan_trail.actions.add_point_by_coordinate') }}</span>
        </button>
        <button
            class="tool-list__item"
            @click="addControlPoints($event)"
            :disabled="!hasRouteCoordinates"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.add_evenly_spaced_control_points_along')"
          >
          <span class="material-icons tool-list__icon">format_line_spacing</span>
          <span class="tool-list__label">{{ $t('plan_trail.actions.add_control_points') }}</span>
        </button>
        <button
            class="tool-list__item"
            @click="launchFlyoverForAllRoutes"
            :disabled="!hasRouteCoordinates"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.3d_flyover_of_all_routes')"
          >
          <span class="material-icons tool-list__icon">flight</span>
          <span class="tool-list__label">{{ $t('shared.3d_flyover') }}</span>
        </button>
        <button
            class="tool-list__item"
            @click="handleMergeAllRoutes"
            :disabled="mergeableRouteCount < 2"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.merge_all_routes_tooltip')"
          >
          <span class="material-icons tool-list__icon">merge</span>
          <span class="tool-list__label">{{ $t('plan_trail.actions.merge_all_routes') }}</span>
        </button>
        <button
            class="tool-list__item"
            @click="showTurnInstructions"
            :disabled="!activeRouteHasInstructions"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-custom-class="weather-tooltip"
            :title="$t('plan_trail.actions.every_turn_on_the_active_route')"
          >
          <span class="material-icons tool-list__icon">turn_right</span>
          <span class="tool-list__label">{{ $t('plan_trail.actions.view_turn_by_turn_instructions') }}</span>
        </button>

        <!-- Manage Control Points dropdown — same row style with chevron -->
        <div class="cp-menu-wrapper" ref="cpMenuRef">
          <button
            class="tool-list__item tool-list__item--toggle"
            :class="{ 'tool-list__item--active': isAreaSelecting }"
            @click="cpMenuOpen = !cpMenuOpen"
            :aria-expanded="cpMenuOpen ? 'true' : 'false'"
          >
            <span class="material-icons tool-list__icon">tune</span>
            <span class="tool-list__label">{{ isAreaSelecting ? $t('plan_trail.actions.selecting_area') : $t('plan_trail.actions.manage_control_points') }}</span>
            <span class="material-icons tool-list__chevron">{{ cpMenuOpen ? 'expand_less' : 'expand_more' }}</span>
          </button>

          <div v-show="cpMenuOpen" class="cp-dropdown">
            <button
              class="cp-dropdown__item"
              :disabled="activeRouteMiddleMarkerCount === 0"
              @click="onRemoveAllControlPoints"
            >
              <span class="material-icons cp-dropdown__icon">commit</span>
              {{ $t('plan_trail.actions.remove_all_control_points') }}
            </button>
            <button
              class="cp-dropdown__item"
              :class="{ 'cp-dropdown__item--active': isAreaSelecting }"
              :disabled="!isAreaSelecting && activeRouteMiddleMarkerCount === 0"
              @click="onSelectArea"
            >
              <span class="material-icons cp-dropdown__icon">highlight_alt</span>
              {{ isAreaSelecting ? $t('plan_trail.actions.cancel_selection') : $t('plan_trail.actions.select_area') }}
            </button>
          </div>

          <!-- Confirmation bar shown after area is drawn -->
          <div v-if="areaSelectedCount > 0" class="selection-confirm">
            <span class="selection-confirm__label">
              {{ $t('plan_trail.actions.area_selected_count_point_area_selected', { area_selected_count: areaSelectedCount, area_selected_count_2: areaSelectedCount === 1 ? '' : 's' }) }}
            </span>
            <div class="selection-confirm__actions">
              <button class="btn btn-danger btn-sm" @click="handleDeleteAreaSelected">{{ $t('shared.delete') }}</button>
              <button class="btn btn-secondary btn-sm" @click="handleExitAreaSelect">{{ $t('shared.cancel_2') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- ───────────────────── Add Point by Coordinate Modal ───────────────────── -->
    <Teleport to="body">
      <div
        ref="addPointModalRef"
        class="modal fade"
        tabindex="-1"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content" style="background:#1b1b1d;color:#fff;border:1px solid rgba(255,255,255,0.1);">
            <div class="modal-header" style="border-bottom-color:rgba(255,255,255,0.08);">
              <h5 class="modal-title"><strong>{{ $t('plan_trail.actions.add_point_by_coordinate_2') }}</strong></h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <select
                  v-model="coordFormat"
                  class="form-select rounded-pill"
                  style="background:#2a2b33;border-color:rgba(255,255,255,0.12);color:#fff;"
                >
                  <option value="latlon">{{ $t('plan_trail.actions.lat_long') }}</option>
                  <option value="ukgrid">{{ $t('plan_trail.actions.uk_grid_reference') }}</option>
                  <option value="irishgrid">{{ $t('plan_trail.actions.irish_grid_reference') }}</option>
                </select>
              </div>
              <div class="mb-3">
                <input
                  ref="coordInputRef"
                  v-model="coordValue"
                  type="text"
                  class="form-control rounded-pill"
                  :placeholder="coordPlaceholder"
                  style="background:#2a2b33;border-color:rgba(255,255,255,0.12);color:#fff;"
                  @keydown.enter="addPointFromCoord"
                />
              </div>
              <div v-if="coordError" class="alert alert-danger py-2 mb-3">{{ coordError }}</div>
            </div>
            <div class="modal-footer" style="border-top-color:rgba(255,255,255,0.08);">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('shared.cancel_2') }}</button>
              <button type="button" class="btn btn-primary" @click="addPointFromCoord">{{ $t('plan_trail.actions.add') }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <hr class="section-divider" />
    </div>

    <div class="actions-main" v-if="panelView === 'instructions'">
      <TurnInstructionsPanel
        :rows="turnInstructionRows"
        :total-distance-meters="activeRouteStats.distance"
        :total-ascent-meters="activeRouteStats.ascent"
        :duration="activeRouteStats.duration"
        @select="handleSelectInstruction"
        @edit="handleEditInstruction"
        @delete="handleDeleteInstruction"
      />
    </div>

    <div class="save-panel">
      <div class="panel-section">
        <button class="btn btn-primary btn-block" @click="saveClicked">
          {{ $t('plan_trail.actions.save_plan') }}
        </button>

        <button class="btn btn-link btn-block text-muted" @click="clearMap">
        {{ $t('shared.clear') }}
        </button>
      </div>
    </div>
  </aside>

  <!-- ───────────────────── Expand button (collapsed) ───────────────────── -->
  <button
    v-if="isCollapsed"
    class="expand-button"
    @click="toggleCollapse"
    :aria-label="$t('plan_trail.actions.expand_actions_panel')"
  >
    <span class="material-icons">right_panel_open</span>
    <span>{{ $t('plan_trail.actions.actions_2') }}</span>
  </button>
</template>

<script setup lang="ts">
/* ───────────────────────── Imports ───────────────────────── */
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { i18n } from '../../../i18n';
import { Modal, Tooltip } from 'bootstrap';
import { OnBoardingGoal } from '../../../utils/models/OnBoardingGoal';
import { plainToClass } from 'class-transformer';
import { LineStringFeature } from '../../../utils/models/LineStringFeature';
import { ensureFlyoverMounted } from '../services/FlyoverMountService';
import mapboxgl, { LngLat } from 'mapbox-gl';
import { GridRef, GridRefUtils } from '../../../utils/helpers/GridRefPreference';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import TurnInstructionsPanel from './TurnInstructionsPanel.vue';
import { DirectionRow } from '../../../utils/models/navigation/directionsList';
import { DirectionsLineString, directionRowAnchor, directionRowsFor, hasInstructions } from '../../../utils/models/navigation/routeDirections';
import { directionRowIcon } from '../../../utils/models/navigation/maneuverIcon';
import { turnFocusZoom } from '../turnFocus';
import { canMapMatch, describeMapMatch } from '../../../utils/models/mapMatch';
import { stepCountLabel } from '../../../utils/models/plannerCueCopy';

/* ───────────────────────── Store / Emits ───────────────────────── */
const store = useStore();

const emit = defineEmits(['save', 'share', 'collapse', 'edit-instruction']);

const cpMenuOpen = ref(false);
const cpMenuRef = ref<HTMLElement | null>(null);

/* ───────────────────────── Local state ───────────────────────── */
const headingId   = `actions-heading-${Math.random().toString(36).slice(2)}`;
const isCollapsed = ref(false);

/* ───────────────────────── Vuex-backed computed ───────────────────────── */
const routePreference = computed({
  get: () => store.state.planTrail.routePreference,
  set: value => store.commit('planTrail/setRoutePreference', value),
});
const isSnap   = computed(() => store.state.planTrail.isSnap);

/**
 *  Four modes:
 *    • 'straight' → isSnap false
 *    • 'hike'     → isSnap true &  routePreference = 'hike'
 *    • 'bike'     → isSnap true &  routePreference = 'bike'
 *    • 'car'      → isSnap true &  routePreference = 'car'
 */
const selectedMode = computed<'straight' | 'hike' | 'bike' | 'car'>(() =>
  isSnap.value ? (routePreference.value as 'hike' | 'bike' | 'car') : 'straight',
);

/* ───────────────────────── Routing button config ───────────────────────── */
const routingModes = [
  { value: 'straight', icon: 'block',            get tooltip() { return i18n.global.t('plan_trail.actions.draw_straight_lines'); } },
  { value: 'hike',     icon: 'hiking',          get tooltip() { return i18n.global.t('plan_trail.actions.snap_to_hiking_paths'); } },
  { value: 'bike',     icon: 'directions_bike',  get tooltip() { return i18n.global.t('plan_trail.actions.snap_to_bike_routes'); } },
  { value: 'car',      icon: 'directions_car',   get tooltip() { return i18n.global.t('plan_trail.actions.snap_to_roads'); } },
] as const;

/* ───────────────────────── 3D Flyover ───────────────────────── */
const hasRouteCoordinates = computed(() => {
  const routesArray = store.getters['planTrail/routesArray'];
  return routesArray.some(route =>
    route.route_style !== 'transport' &&
    route.lineStrings &&
    route.lineStrings.length > 0
  );
});

/* ─────────────────── Turn-by-turn instructions ─────────────────── */

/** The active route's line strings, in the shape the directions reader wants. */
const activeRouteLineStrings = computed<DirectionsLineString[]>(() => {
  const activeKey = store.state.planTrail.activeRouteKey;
  const route = activeKey ? store.state.planTrail.routes[activeKey] : null;
  return (route?.lineStrings ?? []).map((ls: any) => ({
    coordinates: ls.lineStringGeometry?.coordinates ?? [],
    instructions: ls.lineStringProperties?.instructions ?? [],
    profile: ls.lineStringProperties?.profile
  }));
});

// Turns are captured when a segment is snapped, so a hand-drawn route has none and the
// entry says so by being unavailable rather than by opening an empty list.
const activeRouteHasInstructions = computed(() => hasInstructions(activeRouteLineStrings.value));

const turnInstructionRows = ref<DirectionRow[]>([]);

/** Which page the sidebar is showing — the tools, or the turns for the active route. */
const panelView = ref<'actions' | 'instructions'>('actions');

const unitLabel = computed(() =>
  MeasurementPrefUtility.preference().isMetric() ? i18n.global.t('units.metric') : i18n.global.t('units.imperial')
);

/** Distance, ascent and walking time for the active route, as the routes list shows them. */
const activeRouteStats = computed(() => {
  // Matched on the route's own id rather than the store key it happens to be filed under —
  // `routesWithStats` never carries that key, so keying on it silently finds nothing and
  // the strip reads zero for every route.
  const activeId = store.getters['planTrail/activeRoute']?.id;
  const stats = store.getters['planTrail/routesWithStats'].find((r: any) => r.id === activeId);
  return {
    distance: stats?.distance ?? 0,
    ascent: stats?.ascent ?? 0,
    duration: stats?.duration ?? ''
  };
});

const turnInstructionsSubtitle = computed(() =>
  stepCountLabel(turnInstructionRows.value.length, (key, named) => i18n.global.t(key, named ?? {}))
);

function rebuildTurnInstructionRows() {
  turnInstructionRows.value = directionRowsFor(activeRouteLineStrings.value);
  // Follows the panel, which keeps its selection by distance across a rebuild.
  const selected =
    selectedTurnDistance === null
      ? undefined
      : turnInstructionRows.value.find((row) => Math.abs(row.distanceFromStartMeters - selectedTurnDistance!) < 1);
  if (selected) placeTurnMarker(selected);
  else removeTurnMarker();
}

let turnMarker: mapboxgl.Marker | null = null;
let selectedTurnDistance: number | null = null;

function placeTurnMarker(row: DirectionRow): [number, number] | null {
  const map = (window as any).map as mapboxgl.Map | undefined;
  const anchor = directionRowAnchor(activeRouteLineStrings.value, turnInstructionRows.value, row);
  if (!map || !anchor) return null;
  const { at, bearing } = anchor;

  if (!turnMarker) {
    const el = document.createElement('span');
    el.className = 'material-icons turn-marker';
    turnMarker = new mapboxgl.Marker({ element: el, rotationAlignment: 'map' });
  }
  turnMarker.getElement().textContent = directionRowIcon(row);
  turnMarker.setRotation(bearing ?? 0);
  turnMarker.setLngLat(at).addTo(map);
  selectedTurnDistance = row.distanceFromStartMeters;
  return at;
}

function removeTurnMarker() {
  turnMarker?.remove();
  selectedTurnDistance = null;
}

function handleSelectInstruction(row: DirectionRow) {
  const at = placeTurnMarker(row);
  if (!at) return;
  const map = (window as any).map as mapboxgl.Map;
  map.easeTo({ center: at, zoom: turnFocusZoom(map.getZoom()), duration: 400 });
}

watch(panelView, (view) => {
  if (view !== 'instructions') removeTurnMarker();
});

/**
 * Anything that touched the active route, cheaply.
 *
 * Every route-touching mutation sets `updated_at`, including the one an instruction edit
 * commits.
 */
const activeRouteStamp = computed(() => {
  const key = store.state.planTrail.activeRouteKey;
  const route = key ? store.state.planTrail.routes[key] : null;
  return `${key ?? ''}:${route?.updated_at ?? ''}:${route?.lineStrings?.length ?? 0}`;
});

/**
 * Rebuild while the list is open, and only while it is open.
 *
 * Not just for our own edits. Dragging a marker re-snaps a segment and replaces its stored
 * turns wholesale, so rows built earlier keep origins that now address different entries —
 * and the next edit would write, confidently, to the wrong turn. Watching the route rather
 * than rebuilding after each dispatch is what covers that.
 *
 * The walk still only happens while the page is up, which was the reason for building on
 * open in the first place.
 */
watch(activeRouteStamp, () => {
  if (panelView.value === 'instructions') rebuildTurnInstructionRows();
});

function showTurnInstructions() {
  rebuildTurnInstructionRows();
  panelView.value = 'instructions';
}

/**
 * Reopen the cue dialog on a turn that already exists.
 *
 * The values come from storage rather than from the row that was tapped: a row's wording may
 * have been composed from its sign, and pre-filling the field with that would turn "leave it
 * alone" into "adopt the machine's sentence as mine".
 */
function handleEditInstruction(row: DirectionRow) {
  const origin = row.origin;
  const routeId = store.getters['planTrail/activeRoute']?.id;
  if (!origin || routeId === undefined) return;

  const stored = activeRouteLineStrings.value[origin.segmentIndex]
    ?.instructions?.[origin.entryIndex];
  if (!stored) return;

  emit('edit-instruction', {
    mode: 'edit',
    routeId,
    segmentIndex: origin.segmentIndex,
    entryIndex: origin.entryIndex,
    sign: stored.usign ?? stored.sign,
    // Their own words only — never the sentence the row composed from the sign.
    text: stored.utext ?? '',
    kind: stored.cue,
    instruction: row.instruction
  });
}

function handleDeleteInstruction(row: DirectionRow) {
  const origin = row.origin;
  const routeId = store.getters['planTrail/activeRoute']?.id;
  if (!origin || routeId === undefined) return;

  // Unlike the phones, nothing here undoes an instruction edit, so the confirm is the only
  // thing standing between a misclick and losing what somebody wrote.
  if (!window.confirm(`Delete "${row.instruction}"?`)) return;

  store.dispatch('planTrail/deleteInstruction', {
    routeId,
    segmentIndex: origin.segmentIndex,
    entryIndex: origin.entryIndex
  });
}

const mergeableRouteCount = computed(() => {
  const routesArray = store.getters['planTrail/routesArray'];
  return routesArray.filter(r => !r.route_style || r.route_style === 'route').length;
});

const activeRouteMiddleMarkerCount = computed(() => {
  const activeKey = store.state.planTrail.activeRouteKey;
  if (!activeKey) return 0;
  const route = store.state.planTrail.routes[activeKey];
  if (!route) return 0;
  return Math.max(0, route.markers.length - 2);
});

async function handleMergeAllRoutes() {
  await store.dispatch('planTrail/mergeAllRoutes');
}

async function addControlPoints(event: MouseEvent) {
  // Dismiss the hover tooltip now: adding control points re-routes, which
  // briefly clears the route's lineStrings and disables this button. A disabled
  // element stops firing mouseleave, so Bootstrap would otherwise leave the
  // tooltip stuck open. Hide it up front (currentTarget is still valid before
  // the await).
  const trigger = event.currentTarget as Element | null;
  if (trigger) Tooltip.getInstance(trigger)?.hide();
  await store.dispatch('planTrail/addEvenlySpacedControlPoints');
}

const isAreaSelecting = computed(() => store.state.planTrail.isAreaSelecting);
const areaSelectedCount = computed(() => store.state.planTrail.areaSelectedMarkers.length);

function onRemoveAllControlPoints() {
  cpMenuOpen.value = false;
  handleRemoveAllControlPoints();
}

function onSelectArea() {
  cpMenuOpen.value = false;
  if (isAreaSelecting.value) {
    store.dispatch('planTrail/exitAreaSelectMode');
  } else {
    store.dispatch('planTrail/enterAreaSelectMode');
  }
}

function handleExitAreaSelect() {
  store.dispatch('planTrail/exitAreaSelectMode');
}

async function handleDeleteAreaSelected() {
  await store.dispatch('planTrail/deleteAreaSelectedMarkers');
}

function handleCpMenuClickOutside(e: MouseEvent) {
  if (cpMenuRef.value && !cpMenuRef.value.contains(e.target as Node)) {
    cpMenuOpen.value = false;
  }
}

async function handleRemoveAllControlPoints() {
  const count = activeRouteMiddleMarkerCount.value;
  if (count === 0) return;

  const confirmed = window.confirm(
    count === 1
      ? i18n.global.t('plan_trail.actions.remove_one_control_point_confirm')
      : i18n.global.t('plan_trail.actions.remove_control_points_confirm', { count })
  );
  if (!confirmed) return;

  await store.dispatch('planTrail/removeAllControlPoints');
}

async function launchFlyoverForAllRoutes() {
  if (!(window as any).user) {
    (window as any).onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }

  const routesArray = store.getters['planTrail/routesArray'];
  const features = [];
  routesArray.forEach(route => {
    if (route.route_style === 'transport' || !route.lineStrings || route.lineStrings.length === 0) return;
    let allCoords = [];
    route.lineStrings.forEach(ls => {
      const lineStringFeature = plainToClass(LineStringFeature, ls);
      const coords = lineStringFeature.lineStringGeometry.coordinates;
      if (coords && coords.length > 0) {
        if (allCoords.length > 0) {
          allCoords = allCoords.concat(coords.slice(1));
        } else {
          allCoords = allCoords.concat(coords);
        }
      }
    });
    if (allCoords.length >= 2) {
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: allCoords },
        properties: { name: route.name, routeStyle: route.route_style }
      });
    }
  });
  const fc = { type: "FeatureCollection", features };
  if (fc.features.length === 0) return;

  const ready = await ensureFlyoverMounted();
  if (ready) {
    (window as any).showMapModalForCoordinates(fc);
  }
}

/* ───────────────────────── Methods ───────────────────────── */
function setRoutingMode(mode: 'straight' | 'hike' | 'bike' | 'car') {
  if (mode === 'straight') {
    if (isSnap.value) store.commit('planTrail/toggleSnap');
    return;
  }
  if (!isSnap.value) store.commit('planTrail/toggleSnap');
  if (routePreference.value !== mode) store.commit('planTrail/setRoutePreference', mode);
}

const undoClicked     = () => store.dispatch('planTrail/undo');
const redoClicked     = () => store.dispatch('planTrail/redo');
const loopRoute       = () => store.dispatch('planTrail/loopRoute');
const reverseRoute    = () => store.dispatch('planTrail/reverseFullRoute');
const backtrackRoute  = () => store.dispatch('planTrail/backtrackRoute');

const canMapMatchRoutes = canMapMatch(window as any);
const isMapMatching     = ref(false);
const mapMatchMessage   = ref('');
async function mapMatchRoute() {
  if (isMapMatching.value) return;
  isMapMatching.value = true;
  mapMatchMessage.value = 'Matching…';
  try {
    mapMatchMessage.value = describeMapMatch(await store.dispatch('planTrail/mapMatchActiveRoute'));
  } catch (error: any) {
    mapMatchMessage.value = `Map match failed: ${error?.message || error}`;
  } finally {
    isMapMatching.value = false;
  }
}
// const updateElevation = () => store.dispatch('planTrail/updateElevation');


function addTracerRoute() {
  const input = document.getElementById('file-selector') as HTMLInputElement | null;
  if (!input) return;

  input.type = 'file';
  input.accept = '.gpx,.kml,.geojson,application/gpx+xml,application/vnd.google-earth.kml+xml,application/json';
  input.value = '';

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || !target.files || target.files.length === 0) return;
    const file = target.files[0];

    try {
      await store.dispatch('planTrail/addTracerRoute', file);
    } finally {
      target.value = '';
    }
  };

  input.click();
}

/* ── Save / share / clear ───────────────────────── */
async function saveClicked() {
  if (window.user == null) {
    window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }

  // Check for and remove empty routes (routes with less than 2 markers)
  const routes = store.state.planTrail.routes;
  const emptyRouteKeys: string[] = [];

  Object.keys(routes).forEach(routeKey => {
    const route = routes[routeKey];
    if (route.markers.length < 2) {
      emptyRouteKeys.push(routeKey);
      console.log(`[SAVE] Removing empty route: "${route.name}" (Day ${route.day ?? 'N/A'}, Style: ${route.route_style ?? 'route'}, Markers: ${route.markers.length})`);
    }
  });

  if (emptyRouteKeys.length > 0) {
    console.log(`[SAVE] Found ${emptyRouteKeys.length} empty route(s), removing automatically...`);

    // Remove all empty routes
    for (const routeKey of emptyRouteKeys) {
      store.commit('planTrail/removeRoute', routeKey);
    }

    // Refresh the map display
    await store.dispatch('planTrail/addAllRoutesToMap');

    console.log(`[SAVE] Successfully removed ${emptyRouteKeys.length} empty route(s)`);
  } else {
    console.log('[SAVE] No empty routes found');
  }

  if (store.getters['planTrail/isTransportOnlyPlan']) {
    alert(i18n.global.t('plan_trail.save_share.transport_only_plan'));
    return;
  }

  // Validate that the plan has at least one main route (route type)
  if (!store.getters['planTrail/hasMainRoute']) {
    alert(i18n.global.t('plan_trail.save_share.needs_route_segment'));
    return;
  }

  // Validate that each hike day has at least one main route
  const dayValidation = store.getters['planTrail/validateHikeDaysHaveMainRoute'];
  if (!dayValidation.valid) {
    const dayList = dayValidation.invalidDays.map((d: number) => i18n.global.t('plan_trail.save_share.day_number', { number: d })).join(', ');
    alert(i18n.global.t('plan_trail.save_share.days_need_route_segment', { days: dayList }));
    return;
  }

  const element = document.getElementById('save-plan-dialog');
  const modal   = new Modal(element!);
  modal.show();
  emit('save');
}

function shareClicked() {
  if (window.user == null) {
    window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }
  store.dispatch('planTrail/shareRoute');
  emit('share');
}

const clearMap = () => store.dispatch('planTrail/clearMap');

/* ── Add point by coordinate ───────────────────────── */
const addPointModalRef = ref<HTMLElement | null>(null);
const coordInputRef = ref<HTMLInputElement | null>(null);
const coordFormat = ref('latlon');
const coordValue = ref('');
const coordError = ref('');

const coordPlaceholder = computed(() => {
  switch (coordFormat.value) {
    case 'ukgrid': return i18n.global.t('plan_trail.actions.enter_uk_grid_ref');
    case 'irishgrid': return i18n.global.t('plan_trail.actions.enter_irish_grid_ref');
    default: return i18n.global.t('plan_trail.actions.enter_lat_long');
  }
});

function showAddPointModal() {
  coordValue.value = '';
  coordError.value = '';
  if (addPointModalRef.value) {
    const modal = Modal.getOrCreateInstance(addPointModalRef.value);
    modal.show();
    nextTick(() => coordInputRef.value?.focus());
  }
}

async function parseCoordinate(): Promise<LngLat> {
  // Normalize: collapse whitespace to single spaces, trim
  const input = coordValue.value.replace(/\s+/g, ' ').trim();
  if (!input) throw new Error(i18n.global.t('plan_trail.actions.please_enter_coordinates'));

  switch (coordFormat.value) {
    case 'latlon': {
      const cleaned = input.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      const parts = cleaned.split(' ');
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lon = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          return new LngLat(lon, lat);
        }
      }
      throw new Error(i18n.global.t('plan_trail.actions.invalid_lat_long_format'));
    }
    case 'ukgrid': {
      return await convertGridRefToLngLat(input, 'ukgrid');
    }
    case 'irishgrid': {
      return await convertGridRefToLngLat(input, 'irishgrid');
    }
    default:
      throw new Error('Unknown coordinate format selected');
  }
}

async function convertGridRefToLngLat(input: string, type: 'ukgrid' | 'irishgrid'): Promise<LngLat> {
  console.log('[AddPoint] convertGridRefToLngLat called:', type, input);
  try {
    let result: LngLat;
    if (type === 'ukgrid') {
      const utils = new GridRefUtils(GridRef.GbGrid);
      result = await utils.convertLetteredGBGridRefToLatLng(input);
    } else {
      const utils = new GridRefUtils(GridRef.IeGrid);
      result = await utils.convertLetteredIrishGridRefToLonLat(input);
    }
    console.log('[AddPoint] Conversion result:', result);
    // The GridRefUtils methods return a LngLat, but validate it
    if (!result || isNaN(result.lng) || isNaN(result.lat)) {
      throw new Error(i18n.global.t('plan_trail.actions.conversion_invalid_coordinates'));
    }
    return result;
  } catch (error: any) {
    console.error('[AddPoint] Conversion error:', error);
    const fallback = type === 'ukgrid'
      ? i18n.global.t('plan_trail.actions.could_not_convert_uk_grid_ref')
      : i18n.global.t('plan_trail.actions.could_not_convert_irish_grid_ref');
    throw new Error(error?.message || fallback);
  }
}

async function addPointFromCoord() {
  coordError.value = '';
  try {
    const lngLat = await parseCoordinate();
    await store.dispatch('planTrail/addMarkerAtCoordinates', { lng: lngLat.lng, lat: lngLat.lat });

    // Fly to the added point
    if ((window as any).map) {
      (window as any).map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: 15, essential: true });
    }

    // Close modal
    if (addPointModalRef.value) {
      Modal.getInstance(addPointModalRef.value)?.hide();
    }
  } catch (error: any) {
    coordError.value = error?.message || i18n.global.t('plan_trail.actions.invalid_coordinate_format');
  }
}

/* ── Panel collapse ───────────────────────── */
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
  emit('collapse');
}

/* ── Tooltips + click-outside ───────────────────────── */
onMounted(() => {
  const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...triggers].forEach(el => new Tooltip(el));
  document.addEventListener('click', handleCpMenuClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleCpMenuClickOutside);
  removeTurnMarker();
});
</script>

<style>
/* Unscoped: the marker is created outside this component's template. */
/* The selected row's glyph, outlined so it reads over a pale basemap. */
.turn-marker {
  position: absolute;
  top: 0;
  left: 0;
  font-size: 28px;
  color: #ffd600;
  text-shadow: -1px -1px 0 #17181d, 1px -1px 0 #17181d, -1px 1px 0 #17181d, 1px 1px 0 #17181d;
}
</style>

<style scoped>
/* ───────────────────────── Panel container ───────────────────────── */
.action-panel {
  position: absolute;
  top: 1rem;
  right: 1rem;
  height: 85vh;
  width: 280px;
  background: rgb(27 27 29 / 90%);
  color: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* inner regions handle their own scrolling */
  z-index: 500;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
/* Collapsed state */
.action-panel.collapsed {
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.panel-title { margin: 0; font-size: 1.1rem; font-weight: 600; }
.icon-btn    { background: transparent; border: none; color: inherit; cursor: pointer; padding: .25rem; line-height: 0; }
.icon-btn:hover { color: #ffd600; }

/* The title takes the slack so the back button and the unit badge stay pinned to the ends. */
.panel-title-group { flex: 1 1 auto; margin: 0 0.5rem; min-width: 0; }
.panel-subtitle {
  margin: 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
}
.unit-badge {
  flex: 0 0 auto;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
}

/* ── Sections ── */
.panel-section,
.panel-section-header { padding: 0.5rem 0.85rem; }
.panel-section-header { padding-bottom: 0; }
.section-divider {
  margin: 0.15rem 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.2)
}

.section-eyebrow {
  margin: 0 0 0.75rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1;
}

.actions-main{
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0; /* allow this flex child to shrink so it can scroll */
}

.save-panel {
  flex: 0 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgb(27 27 29 / 95%);
}

/* ── Grid buttons ── */
.button-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: .75em;
  grid-row-gap: .75em;
}

/* ── Square icon button ── */
.square { width: 40px; height: 38px; padding: 0; }

/* ── Expand button when collapsed ── */
.expand-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--dark);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 499;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: background 0.2s;
}
.expand-button:hover { background: #2c2c2e; }

/* ── Scrollbar (now lives on the inner actions region) ── */
.actions-main::-webkit-scrollbar { width: 6px; }
.actions-main::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15); border-radius: 3px; }
.actions-main::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.25); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .action-panel { right: 0.5rem; width: 280px; }
}

/* ───────────────────────── New circular routing buttons ───────────────────────── */
.routing-group { display: flex; gap: 0.5rem; }
.routing-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--dark);
  color: #ffffff;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}
.routing-btn .material-icons { font-size: 20px; }
.routing-btn:hover  { background: #3c3c3c; }
.routing-btn:disabled { opacity: 0.6; cursor: wait; }
.map-match-message {
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
}
.routing-btn.active {
  background: #ffd600;  /* yellow when active */
  color: #202020;       /* dark icon */
  font-weight: 700;
}

.button-grid.panel-section {
  display: grid !important;         /* force grid, beats stray flex overrides */
  grid-template-columns: 1fr 1fr;   /* two equal columns                     */
}

/* ── Tool list (Tools section) ────────────────────────────── */
.tool-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tool-list__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.5rem 0.55rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.tool-list__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.tool-list__item:hover .tool-list__icon {
  color: #ffd600;
}

.tool-list__item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tool-list__item:disabled:hover {
  background: transparent;
}

.tool-list__item:disabled:hover .tool-list__icon {
  color: rgba(255, 255, 255, 0.7);
}

.tool-list__icon {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.75);
  flex-shrink: 0;
  transition: color 0.15s;
}

.tool-list__label {
  flex: 1 1 auto;
  min-width: 0;
}

.tool-list__chevron {
  font-size: 18px;
  opacity: 0.6;
  flex-shrink: 0;
  margin-left: auto;
}

.tool-list__item--toggle[aria-expanded="true"] .tool-list__chevron {
  opacity: 1;
}

.tool-list__item--active {
  background: rgba(253, 223, 0, 0.1);
  color: #fddf00;
}

.tool-list__item--active .tool-list__icon { color: #fddf00; }
.tool-list__item--active:hover { background: rgba(253, 223, 0, 0.18); }

.cp-menu-wrapper {
  position: relative;
}

.cp-dropdown {
  margin-top: 0.35rem;
  background: #2f303b;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.cp-dropdown__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.cp-dropdown__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.cp-dropdown__item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cp-dropdown__item--active {
  color: #fddf00;
}

.cp-dropdown__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.selection-confirm {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: rgba(253, 223, 0, 0.1);
  border: 1px solid rgba(253, 223, 0, 0.4);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selection-confirm__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #fddf00;
}

.selection-confirm__actions {
  display: flex;
  gap: 0.5rem;
}

.selection-confirm__actions .btn {
  flex: 1;
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem;
}
</style>
